import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend, ResponsiveContainer,
} from 'recharts';
import { Card } from './ui/card';
import InfoTooltip from './InfoTooltip';
import api from '../lib/api';
import { fmtDate } from '../lib/date';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

// ─────────────────────────────────────────────────────────────────────────
// Eval → Session Conversion chart (iter 90).
//
// Requested by a clinician: "how many of the evals turn into sessions?"
// The existing conversion funnel expresses everything as % of referrals,
// which hides the eval-to-therapy step under two other stages. This chart
// isolates that step per month.
//
// Iter 256 — every data point (both bar series and the conversion dot)
// is clickable. Clicking opens a modal that fetches the client list
// behind that month × clinic bucket from `/api/dashboard/eval-to-session/detail`.
// Works for ALL clinics — the scope dropdown and click both propagate
// the same `clinic` value to the backend; nothing is hardcoded to a
// single clinic name any more.
//
// Data shape per row: { month, evals_completed, therapy_started, conversion_pct }
//
// Props:
//   - data:            monthly array for the current scope (org-wide or one clinic)
//   - byClinic:        { clinicName: monthlyArray } — optional; when passed,
//                      the header renders a dropdown to switch between "All"
//                      and any single clinic. Pass this on Executive BI, skip
//                      it on ClinicView (where the scope is fixed).
//   - defaultLabel:    header suffix shown when byClinic is not provided —
//                      e.g. `Katy`. Falls back to `All clinics`.
// ─────────────────────────────────────────────────────────────────────────
export function EvalToSessionChart({ data, byClinic, defaultLabel, testId }) {
  const hasByClinic = byClinic && Object.keys(byClinic).length > 0;
  const [scope, setScope] = useState('all');
  const [drill, setDrill] = useState(null);   // { month, clinicScope }
  const clinicOptions = useMemo(
    () => Object.keys(byClinic || {}).sort(),
    [byClinic]
  );
  const rows = useMemo(() => {
    if (!hasByClinic) return data || [];
    if (scope === 'all') return data || [];
    return byClinic[scope] || [];
  }, [scope, data, byClinic, hasByClinic]);

  // Aggregate: average conversion + total evals in view
  const totals = useMemo(() => {
    const evals = rows.reduce((a, r) => a + (r.evals_completed || 0), 0);
    const started = rows.reduce((a, r) => a + (r.therapy_started || 0), 0);
    return {
      evals,
      started,
      avgPct: evals > 0 ? Math.round((1000 * started) / evals) / 10 : 0,
    };
  }, [rows]);

  const scopeLabel = hasByClinic
    ? scope === 'all' ? 'All clinics' : scope
    : defaultLabel || 'All clinics';

  // Effective clinic value for /detail requests. When the parent didn't
  // supply byClinic (ClinicView), we fall back to defaultLabel — that's
  // the clinic the page is scoped to.
  const effectiveClinic = hasByClinic
    ? (scope === 'all' ? '' : scope)
    : (defaultLabel && defaultLabel.toLowerCase() !== 'all clinics'
        ? defaultLabel : '');

  const openDrill = (month) => {
    if (!month) return;
    setDrill({ month, clinic: effectiveClinic, scopeLabel });
  };

  if (!rows || rows.length === 0) {
    return (
      <Card
        className="rounded-2xl border p-5"
        style={{ borderColor: '#E8E5EC', background: '#FAF8FB' }}
        data-testid={testId || 'eval-to-session-chart-empty'}
      >
        <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>
          Eval → Session Conversion
        </div>
        <div className="text-sm mt-2" style={{ color: '#685B72' }}>
          Not enough eval data yet for {scopeLabel}. Once a few evals are
          completed the trend will appear here.
        </div>
      </Card>
    );
  }

  return (
    <>
    <Card
      className="rounded-2xl border p-0 overflow-hidden"
      style={{ borderColor: '#E8E5EC' }}
      data-testid={testId || 'eval-to-session-chart'}
    >
      {/* Header — dark, matches ConversionFunnel styling for visual pairing */}
      <div
        className="px-4 py-2 flex items-center justify-between gap-2 text-white flex-wrap"
        style={{ background: '#685B72' }}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold">
            Eval → Session Conversion
          </span>
          <span className="text-[10px] opacity-70">
            (How many completed evals turn into ongoing therapy — {scopeLabel})
          </span>
          <InfoTooltip testId="tt-eval-to-session" text="Each bar shows the number of evals completed that month at the selected clinic and the therapy starts that came out of those families. The gold line = therapy-start % (starts ÷ evals). Click any bar or dot to see the client list." className="text-white/70 hover:text-white" />
        </div>
        {hasByClinic && clinicOptions.length > 0 && (
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="text-[11px] rounded-md px-2 py-1 bg-white/10 text-white border border-white/20 hover:bg-white/20 cursor-pointer"
            data-testid="eval-to-session-scope"
          >
            <option value="all" className="text-[#2D2833]">All clinics</option>
            {clinicOptions.map((cn) => (
              <option key={cn} value={cn} className="text-[#2D2833]">{cn}</option>
            ))}
          </select>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-0 border-b" style={{ borderColor: '#EDE9F0' }}>
        <div className="p-3 text-center border-r" style={{ borderColor: '#EDE9F0' }}>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>Evals Completed</div>
          <div className="font-serif text-xl mt-0.5" style={{ color: '#2D2833' }}>{totals.evals.toLocaleString()}</div>
        </div>
        <div className="p-3 text-center border-r" style={{ borderColor: '#EDE9F0' }}>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>Started Therapy</div>
          <div className="font-serif text-xl mt-0.5" style={{ color: '#839E97' }}>{totals.started.toLocaleString()}</div>
        </div>
        <div className="p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>Conversion Avg</div>
          <div className="font-serif text-xl mt-0.5" style={{ color: '#B08A3A' }} data-testid="eval-to-session-avg-pct">{totals.avgPct}%</div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-4">
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart
            data={rows}
            margin={{ top: 8, right: 10, left: 0, bottom: 0 }}
            onClick={(state) => {
              // Recharts hands us the active label (= month) when the
              // click lands on any series inside the plot area.
              const m = state && state.activeLabel;
              if (m) openDrill(m);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#EDE9F0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: '#685B72' }}
              tickFormatter={(m) => {
                // 2026-04 → Apr 26
                if (!m || m.length < 7) return m;
                const [y, mm] = m.split('-');
                const short = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                return `${short[parseInt(mm, 10) - 1] || m} ${y.slice(-2)}`;
              }}
            />
            <YAxis
              yAxisId="count"
              tick={{ fontSize: 11, fill: '#685B72' }}
              width={40}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="pct"
              orientation="right"
              tick={{ fontSize: 11, fill: '#B08A3A' }}
              width={40}
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              cursor={{ fill: '#F1EDF5' }}
              contentStyle={{ borderRadius: 10, borderColor: '#E8E5EC', fontSize: 12 }}
              formatter={(value, name) => {
                if (name === 'Conversion') return [`${value}%`, name];
                return [value, name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              iconType="circle"
              iconSize={8}
            />
            <Bar
              yAxisId="count" dataKey="evals_completed" name="Evals Completed"
              fill="#685B72" radius={[6, 6, 0, 0]} maxBarSize={28}
              cursor="pointer"
              onClick={(p) => openDrill(p && p.month)}
            />
            <Bar
              yAxisId="count" dataKey="therapy_started" name="Started Therapy"
              fill="#839E97" radius={[6, 6, 0, 0]} maxBarSize={28}
              cursor="pointer"
              onClick={(p) => openDrill(p && p.month)}
            />
            <Line
              yAxisId="pct" type="monotone" dataKey="conversion_pct" name="Conversion"
              stroke="#B08A3A" strokeWidth={2.5}
              dot={{ r: 3, fill: '#B08A3A' }}
              activeDot={{
                r: 6,
                stroke: '#B08A3A',
                strokeWidth: 2,
                fill: '#fff',
                style: { cursor: 'pointer' },
                onClick: (_e, payload) => openDrill(payload && payload.payload && payload.payload.month),
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="text-[10px] mt-2 text-center" style={{ color: '#685B72' }}>
          Click any bar or dot to see the clients behind that month.
        </div>
      </div>
    </Card>

    <EvalToSessionDetailModal
      open={!!drill}
      onClose={() => setDrill(null)}
      month={drill?.month}
      clinic={drill?.clinic}
      scopeLabel={drill?.scopeLabel}
    />
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Drill-down modal — client list behind a single data point.
// Iter 256. Backed by GET /api/dashboard/eval-to-session/detail.
// ─────────────────────────────────────────────────────────────────────────
function EvalToSessionDetailModal({ open, onClose, month, clinic, scopeLabel }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Iter 261 — clicking a patient name closes the drilldown and jumps
  // to the client detail via the Pipeline page's `?open=<id>` deep
  // link (same route used by Inquiries → Convert and every other
  // "open this client" surface). Falls back to the client_id label
  // when the name is blank so unnamed rows are still navigable.
  const openClient = (clientId) => {
    if (!clientId) return;
    onClose();
    navigate(`/pipeline?open=${encodeURIComponent(clientId)}`);
  };

  useEffect(() => {
    if (!open || !month) return undefined;
    let alive = true;
    setLoading(true);
    setError('');
    setDetail(null);
    (async () => {
      try {
        const params = { month };
        if (clinic) params.clinic = clinic;
        const { data } = await api.get('/dashboard/eval-to-session/detail', { params });
        if (alive) setDetail(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e?.message || 'Failed to load detail');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, month, clinic]);

  const label = month ? monthLabel(month) : '';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl"
        data-testid="eval-to-session-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-lg text-[#2D2833]">
            Eval → Session — {label}
          </DialogTitle>
          <DialogDescription className="text-xs" style={{ color: '#685B72' }}>
            {/* Iter 256b — prefer explicit clinic > scopeLabel > fallback.
                Fixes ClinicView modal always showing "All clinics" even
                when scoped to a single site. */}
            {clinic || scopeLabel || 'All clinics'}
            {detail && (
              <span>
                {' · '}
                <span data-testid="e2s-detail-total-evals">{detail.evals_completed}</span> evals completed,{' '}
                <span data-testid="e2s-detail-total-started">{detail.therapy_started}</span> started therapy
                {' · '}
                <span data-testid="e2s-detail-conversion-pct">{detail.conversion_pct}%</span> conversion
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-sm py-8 text-center" style={{ color: '#685B72' }}
               data-testid="e2s-detail-loading">
            Loading clients…
          </div>
        )}
        {!loading && error && (
          <div className="text-sm py-6 text-center" style={{ color: '#8A3A3A' }}
               data-testid="e2s-detail-error">
            {error}
          </div>
        )}
        {!loading && !error && detail && detail.clients.length === 0 && (
          <div className="text-sm py-8 text-center" style={{ color: '#685B72' }}
               data-testid="e2s-detail-empty">
            No completed evals for {label} in {clinic || scopeLabel || 'this scope'}.
          </div>
        )}
        {!loading && !error && detail && detail.clients.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto rounded-md border"
               style={{ borderColor: '#EDE9F0' }}>
            <table className="w-full text-xs" data-testid="e2s-detail-table">
              <thead>
                <tr className="text-left uppercase tracking-widest text-[10px] sticky top-0"
                    style={{ background: '#F7F4FA', color: '#685B72' }}>
                  <th className="py-2 px-3 font-semibold">Patient</th>
                  <th className="py-2 px-3 font-semibold">Eval Date</th>
                  <th className="py-2 px-3 font-semibold">Eval Clinic</th>
                  <th className="py-2 px-3 font-semibold">Therapy Start</th>
                  <th className="py-2 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {detail.clients.map((c, i) => {
                  const name = (c.name && c.name.trim()) || '(unnamed)';
                  const hasId = !!c.client_id;
                  return (
                  <tr key={c.client_id || i}
                      className="border-t hover:bg-[#FAF7FC]"
                      style={{ borderColor: '#EDE9F0' }}
                      data-testid={`e2s-detail-row-${i}`}>
                    <td className="py-2 px-3">
                      {hasId ? (
                        <button
                          type="button"
                          onClick={() => openClient(c.client_id)}
                          className="text-left underline decoration-dotted underline-offset-2 hover:text-[#685B72] focus:outline-none focus:ring-2 focus:ring-[#685B72] rounded"
                          style={{ color: '#2D2833' }}
                          data-testid={`e2s-detail-open-client-${i}`}
                          title="Open client details"
                        >
                          {name}
                        </button>
                      ) : (
                        <span style={{ color: '#2D2833' }}>{name}</span>
                      )}
                    </td>
                    <td className="py-2 px-3" style={{ color: '#685B72' }}>{fmtDate(c.eval_date)}</td>
                    <td className="py-2 px-3" style={{ color: '#685B72' }}>{c.eval_clinic || '—'}</td>
                    <td className="py-2 px-3" style={{ color: '#685B72' }}>
                      {c.therapy_start_date ? fmtDate(c.therapy_start_date) : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{
                          background: c.therapy_started ? '#E4EEE9' : '#F5EFDD',
                          color: c.therapy_started ? '#3B6F5D' : '#8A6A1F',
                        }}
                      >
                        {c.eval_status}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function monthLabel(m) {
  if (!m || m.length < 7) return m || '';
  const [y, mm] = m.split('-');
  const full = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
  return `${full[parseInt(mm, 10) - 1] || m}, ${y}`;
}

export default EvalToSessionChart;
